import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { CreateMetricDto, FetchError, MetricAnnotationDto, MetricCreatedDto, MetricDatasetDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  newMetricGuid: string;
  teamSlug: string;
  title: string;
  description: string;
  assignedTeamSlug?: string;
  reportingUserGuid?: string;
  sortOrder: string;
  datasets: MetricDatasetDto[];
  annotations?: MetricAnnotationDto[];
};

const useCreateMetricRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createMetricRequest(variables: Variables): Promise<MetricCreatedDto> {
    const accessToken = await getAccessToken();
    const body: CreateMetricDto = {
      guid: variables.newMetricGuid,
      title: variables.title,
      description: variables.description,
      assignedTeamSlug: variables.assignedTeamSlug,
      reportingUserGuid: variables.reportingUserGuid,
      sortOrder: variables.sortOrder,
      datasets: variables.datasets,
      annotations: variables.annotations,
    };

    const response = await fetch(`${configuration.apiRootUrl}/metrics`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { createMetricRequest };
};

const useCreateMetric = () => {
  const { createMetricRequest } = useCreateMetricRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMetricRequest,
    onSuccess: async (_, variables) => {
      if (variables.assignedTeamSlug) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.teams.getTeam(variables.assignedTeamSlug) });
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.metrics.getMetricCollections() });
    },
  });
};

export { useCreateMetric };
